import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const type = params.get("type");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      while (count < 8 && !cancelled) {
        count++;
        setAttempts(count);
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            setStatus("paid");
            await refresh();
            return;
          }
          if (data.status === "expired" || data.payment_status === "failed") {
            setStatus("failed");
            return;
          }
        } catch {
          setStatus("error");
          return;
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <div className="max-w-md w-full module-card p-10 bg-white text-center">
        {status === "polling" && (
          <>
            <Loader2 className="mx-auto text-sage-deep animate-spin" size={40} />
            <h1 className="font-heading text-2xl font-semibold mt-4">Confirming payment...</h1>
            <p className="text-sm text-muted-foreground mt-2">Attempt {attempts}/8</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-sage-deep" size={48} />
            <h1 className="font-heading text-2xl font-semibold mt-4" data-testid="payment-success-title">Payment confirmed</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {type === "subscription" ? "Your access is active for the next 30 days." : "Your order has been placed."}
            </p>
            <button
              data-testid="payment-continue-button"
              onClick={() => navigate(type === "subscription" ? "/dashboard" : "/buying")}
              className="mt-6 w-full py-3 rounded-md bg-ink text-bone font-medium"
            >
              Continue
            </button>
          </>
        )}
        {(status === "failed" || status === "timeout" || status === "error") && (
          <>
            <XCircle className="mx-auto text-destructive" size={48} />
            <h1 className="font-heading text-2xl font-semibold mt-4">
              {status === "timeout" ? "Still processing..." : "Payment not completed"}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              {status === "timeout"
                ? "We'll keep checking in the background. Refresh in a moment."
                : "If you were charged, contact support. Otherwise try again."}
            </p>
            <Link to="/paywall" className="mt-6 block w-full py-3 rounded-md border border-ink/15">
              Back to access page
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
